import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
  ViewChild,
  AfterViewInit,
  effect,
} from '@angular/core';
import { SetupNodeComponent } from '../../setup-node/setup-node.component';
import { DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxValidatorComponent, DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { DxFormComponent, DxiFormItemComponent, DxoFormLabelComponent } from 'devextreme-angular/ui/form';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { DxPopoverComponent } from 'devextreme-angular/ui/popover';
import { DxNumberBoxModule } from 'devextreme-angular/ui/number-box';
import { Milestone, Phase } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-milestone-node',
    imports: [
        CommonModule,
        SetupNodeComponent,
        DxValidatorModule,
        DxiValidationRuleComponent,
        SvgIconComponent,
        DxFormComponent,
        DxiFormItemComponent,
        DxPopoverComponent,
        DxTextBoxModule,
        DxNumberBoxModule,
        DxoFormLabelComponent,
    ],
    templateUrl: './setup-milestone-node.component.html',
    styleUrl: './setup-milestone-node.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupMilestoneNodeComponent implements AfterViewInit {
  readonly messageWeighting = $localize`:@@weighting:Weighting`;

  // Bind the validation callback in constructor to ensure proper context
  public uniqueNameValidationCallback: (params: any) => boolean;

  milestone = model.required<Milestone>();
  phase = model.required<Phase>();
  phases = model.required<Phase[]>();
  editing = model<boolean>();
  expanded = model<boolean>(true);
  validationGroup = input<string>();
  allowDelete = input<boolean>();
  onDelete = output<Milestone>();

  private changes: boolean = false;
  public isValid: boolean = null;
  private callbacks: Array<() => void> = [];

  adapterConfig = {
    getValue: () => this.validateForm(),
    validationRequestsCallbacks: this.callbacks,
  };

  @ViewChild(DxFormComponent)
  form!: DxFormComponent;

  constructor() {
    // Bind the validation callback to ensure proper context
    this.uniqueNameValidationCallback = this.validateUniqueName.bind(this);

    // Watch for changes in phases signal and trigger re-validation
    this.phases.subscribe(() => {
      // Force re-validation when phases change
      if (this.form?.instance) {
        this.form.instance.validate();
      }
      this.changes = true; // Force validation callback to re-run
    });
  }

  // Validation callback for the custom validation rule
  validationCallback = () => {
    const isValid = this.validateForm();
    return isValid;
  };

  ngAfterViewInit() {}

  private validateForm(): boolean {
    if (this.form?.instance) {
      const formValidation = this.form.instance.validate();
      const isValid = formValidation?.isValid ?? false;

      // Only trigger callbacks if validation state changed
      if (this.isValid !== isValid) {
        this.isValid = isValid;
        this.revalidate();
      }

      return isValid;
    }
    return false;
  }

  private revalidate() {
    this.callbacks.forEach(func => {
      func();
    });
  }

  onDataChanged() {
    // Trigger validation update when data changes
    this.isValid = null; // Force revalidation
    this.validateForm();
  }

  onDeleteClicked() {
    this.onDelete.emit(this.milestone());
  }

  /**
   * Method version of the unique name validation for proper binding
   */
  private validateUniqueName(params: any): boolean {
    console.log('Validate Unique Name');

    const currentName = params?.value || params?.data;

    // If no name entered, let the required validation handle it
    if (!currentName || currentName.trim() === '') {
      return true;
    }

    // Get the current milestone to exclude it from duplicate check
    const currentMilestone = this.milestone();
    const currentPhase = this.phase();
    const allPhases = this.phases();

    // Check if any milestone in any phase has the same name (case-insensitive)
    const duplicateExists = allPhases.some(phase => {
      if (!phase.milestones || phase.milestones.length === 0) {
        return false;
      }

      return phase.milestones.some(milestone => {
        // Skip the current milestone we're editing (using id comparison)
        if (milestone.id === currentMilestone.id) {
          return false;
        }

        const hasName = milestone.name;
        const isSameName = hasName && milestone.name.trim().toLowerCase() === currentName.trim().toLowerCase();

        return hasName && isSameName;
      });
    });

    return !duplicateExists;
  }
}
